import { createFileRoute, Outlet, redirect, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect } from "react";

import { supabase } from "@/integrations/supabase/client";
import { ensureProfile } from "@/lib/jobs.functions";
import { claimInvite } from "@/lib/sharing.functions";
import { PENDING_INVITE_TOKEN_KEY } from "@/routes/invite.$token";

export const Route = createFileRoute("/_authenticated")({
  ssr: false,
  beforeLoad: async () => {
    const { data, error } = await supabase.auth.getUser();
    if (error || !data.user) throw redirect({ to: "/auth" });
    return { user: data.user };
  },
  component: AuthenticatedLayout,
});

/**
 * Someone who wasn't signed in yet when they opened an invite link gets sent
 * through /auth, which always lands on /dashboard afterward (its own
 * redirect targets aren't something this feature needed to change — see
 * invite.$token.tsx). This picks the pending token back up on whichever
 * authenticated page loads first post-sign-in and finishes the claim there,
 * once, silently — a stale/already-claimed token is not an error worth
 * surfacing on an unrelated page.
 */
function AuthenticatedLayout() {
  const navigate = useNavigate();
  const claim = useServerFn(claimInvite);
  const makeProfile = useServerFn(ensureProfile);

  // Every signed-in visitor gets a profile row, once per browser session. Until now ensureProfile only ran from Upload and Account, so someone who
  // signed up through a shared-report invitation and only ever opened that report had an account but no profile: absent from the admin Members list,
  // no new-signup notice (found 2026-10-07 with paul.imbusch@posteo.de). The call is an idempotent upsert; a failure must never disturb the page.
  useEffect(() => {
    let cancelled = false;
    supabase.auth.getUser().then(({ data }) => {
      const uid = data.user?.id;
      if (!uid || cancelled) return;
      const key = `understandyourgenome:profile-ensured:${uid}`;
      try {
        if (sessionStorage.getItem(key)) return;
      } catch {
        /* storage blocked: just call it */
      }
      makeProfile()
        .then(() => {
          try {
            sessionStorage.setItem(key, "1");
          } catch {
            /* ignore */
          }
        })
        .catch(() => {
          /* best effort */
        });
    });
    return () => {
      cancelled = true;
    };
  }, [makeProfile]);

  useEffect(() => {
    const token = localStorage.getItem(PENDING_INVITE_TOKEN_KEY);
    if (!token) return;
    localStorage.removeItem(PENDING_INVITE_TOKEN_KEY);
    claim({ data: { token } })
      .then((result) => navigate({ to: "/report/$jobId", params: { jobId: result.jobId } }))
      .catch(() => {
        /* already claimed, revoked, or invalid — nothing to do here */
      });
  }, [claim, navigate]);

  return <Outlet />;
}
